import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { lrwSessionStore } from 'store';
import { OnComputerWritingFormat } from 'api/planning';
import { Timestamp } from '@britishcouncil/common-core';
import { useEffect, useMemo } from 'react';
import { useIntParam, useNavigationContext, useSafeMutation, useServerErrors } from 'hooks';
import { parseServerErrors } from 'utils';
import { useNavigate, generatePath } from 'react-router';
import { ROUTING_PATHS } from 'const';
import { LrwSessionFormValues, lrwSessionFormSchema as schema } from '../common/lrw-session-form';
import {
  fromApiSessionEndTime,
  fromApiSessionStartTime,
  getMinLrwSessionEndTime,
  toApiSessionTime,
} from '../common/lrw-session-form/lrw-session-time';
import { useWritingFormatToggleCapabilities } from './hooks';

export const useEditLrwSession = () => {
  const sessionId = useIntParam('sessionId');
  const { locationId } = useNavigationContext();
  const navigate = useNavigate();
  const { lrwSessionDetails } = ROUTING_PATHS;

  const { data: lrwSession, isFetching: isFetchingSession } =
    lrwSessionStore.service.lrwSessionApi.useGetLrwSessionDetailsQuery({
      lrwSessionId: sessionId,
      locationId,
    });
  const { data: updateData, isFetching: isFetchingEditData } =
    lrwSessionStore.service.useGetLrwSessionUpdateDataQuery({ lrwSessionId: sessionId });
  const venueOptions = useMemo(() => {
    const options = lrwSession ? [{ text: lrwSession.venue, value: lrwSession.venueId }] : [];
    return options;
  }, [lrwSession]);

  const { canToggleOnComputer, canToggleOnPaper } = useWritingFormatToggleCapabilities({
    lrwSession,
    updateCapabilities: updateData?.updateCapabilities,
  });

  const form = useForm<LrwSessionFormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {},
  });

  const [startTime] = form.watch(['sessionStartTime']);

  const minEndTime = getMinLrwSessionEndTime(startTime);

  useEffect(() => {
    if (!lrwSession) {
      return;
    }

    form.reset({
      sessionDate: Timestamp.fromDate(new Date(lrwSession.date)),
      sessionStartTime: fromApiSessionStartTime(lrwSession.startTime),
      sessionEndTime: fromApiSessionEndTime(lrwSession.endTime),
      venueId: lrwSession.venueId,
      testTakerAdmissionTimeMinutes: lrwSession.testTakerAdmissionTimeMinutes,
      onComputerEnabled: lrwSession.onComputerWritingFormats.includes(
        OnComputerWritingFormat.OnComputer
      ),
      onPaperEnabled: lrwSession.onComputerWritingFormats.includes(OnComputerWritingFormat.OnPaper),
    });
  }, [lrwSession, form]);

  const [editLrwSession, { isLoading: isSaving, error: editError }] =
    lrwSessionStore.service.useUpdateLrwSessionMutation();

  const goToLrwSessionDetails = () =>
    navigate(generatePath(lrwSessionDetails.absolutePath, { sessionId }));

  const { execute: executeEditLrwSession } = useSafeMutation(editLrwSession, {
    successToastMessage: 'Changes saved successfully',
    onSuccess: goToLrwSessionDetails,
  });

  const serverErrors = useMemo(() => parseServerErrors(editError), [editError]);

  const { generalErrors, clearServerErrors } = useServerErrors(
    form.getValues(),
    serverErrors,
    form.setError
  );

  const handleSubmit = (data: LrwSessionFormValues) => {
    clearServerErrors();

    executeEditLrwSession({
      lrwSessionId: sessionId,
      request: {
        sessionStartTime: toApiSessionTime(data.sessionStartTime),
        sessionEndTime: toApiSessionTime(data.sessionEndTime),
        testTakerAdmissionTimeMinutes: data.testTakerAdmissionTimeMinutes ?? 0,
        onComputerEnabled: data.onComputerEnabled ?? false,
        onPaperEnabled: data.onPaperEnabled ?? false,
      },
    });
  };

  return {
    form,
    handleSubmit,
    generalErrors,
    venueOptions,
    isFetchingLrwSession: isFetchingSession || isFetchingEditData,
    isSaving,
    minEndTime,
    updateCapabilities: updateData?.updateCapabilities,
    canToggleOnComputer,
    canToggleOnPaper,
  };
};
